import { BooleanOrListOfString } from '../models/core/bool-or-list.js';
import { SeatedOpenAccessProtocolModifier } from '../models/serializable/open-access-pass/seated-open-access-protocol-modifier.js';
import { SeatedOpenAccessProtocol } from '../models/serializable/open-access-pass/seated-open-access-protocol.js';
import { StagedSeatedOpenAccessProtocol } from '../models/serializable/open-access-pass/staged-seated-open-access-pass-protocol.js';
import { SeatingOptions } from '../models/serializable/seating-options.js';
import { OpenAccessPassSeatHelper } from './open-access-pass-seat-helper.js';
import { OpenAccessStageEntryHelper } from './open-access-stage-entry-helper.js';
import { SeatedOpenAccessProtocolHelper } from './seated-open-access-protocol-helper.js';

export type SeatedPassProtocol = SeatedOpenAccessProtocol | StagedSeatedOpenAccessProtocol;

export interface OpenAccessPassSeatSource {
  readonly sourceId: string;
  readonly stageId: string | null;
  readonly entryId: string | null;
  readonly roomId: string;
  readonly seatingStateId: string;
  readonly eventConfiguration: Map<string, BooleanOrListOfString>;
  readonly seatingOptions: SeatingOptions | null;
  readonly modifiers: Map<string, SeatedOpenAccessProtocolModifier>;
  readonly helper: OpenAccessPassSeatHelper;
}

export class OpenAccessPassSeatHelperFactory {
  static forPass({ protocol, passVariationId }: { protocol: SeatedPassProtocol, passVariationId: string }): OpenAccessPassSeatSource[] {
    if(protocol instanceof SeatedOpenAccessProtocol) {
      return [this.legacySource(protocol, SeatedOpenAccessProtocolHelper.forPass({ protocol, passVariationId }))];
    }
    return this.stagedSources(protocol, (stageEntry) => OpenAccessStageEntryHelper.forPass({
      stageEntry,
      eventValidityRule: protocol.eventValidityRule,
      passVariationId,
    }));
  }

  static forTemplate({ protocol }: { protocol: SeatedPassProtocol }): OpenAccessPassSeatSource[] {
    if(protocol instanceof SeatedOpenAccessProtocol) {
      return [this.legacySource(protocol, SeatedOpenAccessProtocolHelper.forTemplate({ protocol }))];
    }
    return this.stagedSources(protocol, (stageEntry) => OpenAccessStageEntryHelper.forTemplate({
      stageEntry,
      eventValidityRule: protocol.eventValidityRule,
    }));
  }

  private static legacySource(protocol: SeatedOpenAccessProtocol, helper: OpenAccessPassSeatHelper): OpenAccessPassSeatSource {
    return {
      sourceId: 'legacy',
      stageId: null,
      entryId: null,
      roomId: protocol.roomId,
      seatingStateId: protocol.roomId,
      eventConfiguration: protocol.eventConfiguration,
      seatingOptions: protocol.seatingOptions,
      modifiers: protocol.modifiers,
      helper,
    };
  }

  private static stagedSources(
      protocol: StagedSeatedOpenAccessProtocol,
      createHelper: (stageEntry: StagedSeatedOpenAccessProtocol['stages'][number]['entries'][number]) => OpenAccessPassSeatHelper,
  ): OpenAccessPassSeatSource[] {
    return protocol.stages.flatMap((stage) => stage.entries.map((entry) => ({
      sourceId: `stage:${stage.uniqueId}:entry:${entry.uniqueId}`,
      stageId: stage.uniqueId,
      entryId: entry.uniqueId,
      roomId: entry.roomId,
      seatingStateId: entry.uniqueId,
      eventConfiguration: entry.eventConfiguration,
      seatingOptions: entry.seatingOptions,
      modifiers: entry.modifiers,
      helper: createHelper(entry),
    })));
  }
}
